import { Component, ChangeDetectionStrategy, inject, effect, AfterViewInit } from '@angular/core';

import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import {
  ButtonComponent,
  CustomDatePickerComponent,
  ModalLayoutComponent,
  SnackbarComponent,
  ToastType,
  } from '@program-suite/ui';
import { UI_FORM_IMPORTS } from '../../../ui-form-imports';
import { ViewSubmissionStore, LookupStore } from '@program-suite/data-common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SubmissionStatusEnum } from '@program-suite/data-common';
import { toDateOnly } from '@program-suite/util-helpers';

@Component({
  selector: 'lib-bind-request',
  templateUrl: './bind-request.component.html',
  styleUrl: './bind-request.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ModalLayoutComponent,
    ReactiveFormsModule,
    FormsModule,
    ButtonComponent,
    ...UI_FORM_IMPORTS,
    CustomDatePickerComponent
],
})
export class BindRequestComponent implements AfterViewInit {
  data:any = inject(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<BindRequestComponent>);
  form: FormGroup;
  viewSubmissionStore = inject(ViewSubmissionStore);
  lookupStore = inject(LookupStore);

  minEffectiveDate: Date;
  maxEffectiveDate: Date;

  constructor(private snackbar: MatSnackBar) {
    // Set effective date constraints: today to 60 days in the future
    const today = new Date();
    this.minEffectiveDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    this.maxEffectiveDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 60);

    // Load states lookup for the dropdown
    this.lookupStore.loadLookup('states');

    const effectiveDateValidator = (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      // Parse the date value - handle both string (YYYY-MM-DD) and Date objects
      let dateOnly: Date;
      if (typeof control.value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(control.value)) {
        // Parse YYYY-MM-DD string directly to avoid timezone issues
        const [year, month, day] = control.value.split('-').map(Number);
        dateOnly = new Date(year, month - 1, day);
      } else {
        const date = new Date(control.value);
        if (isNaN(date.getTime())) return null;
        dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      }

      if (dateOnly < this.minEffectiveDate) {
        return { dateInPast: true };
      }
      if (dateOnly > this.maxEffectiveDate) {
        return { dateTooFar: true };
      }
      return null;
    };

    const baseFormControls: any = {
      application: new FormControl([]),
      lossRuns: new FormControl([]),
      proposedEffectiveDate: new FormControl(new Date(), effectiveDateValidator),
    };

    // Add agency/producer info fields if RetailAgentId is not set
    if (!this.data.retailAgentId) {
      baseFormControls.agencyName = new FormControl('', Validators.required);
      baseFormControls.producerName = new FormControl('', Validators.required);
      baseFormControls.agencyAddress1 = new FormControl('', Validators.required);
      baseFormControls.agencyAddress2 = new FormControl('');
      baseFormControls.agencyCity = new FormControl('', Validators.required);
      baseFormControls.agencyStateCode = new FormControl(null, Validators.required);
      baseFormControls.agencyZipCode = new FormControl('', Validators.required);
    }

    this.form = new FormGroup(baseFormControls);

    effect(() => {
      if (this.viewSubmissionStore.applicationDocuments().length == 0) {
        this.form.get('application')?.addValidators(Validators.required);
        this.form.get('application')?.updateValueAndValidity();
      }
    });
  }

  ngAfterViewInit() {
    this.form.get('proposedEffectiveDate')?.setValue(this.data.policyEffectiveDate);
  }

  submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const applicationForm = new FormData();
    const applicationFiles = this.form.get('application')?.value;
    for (let i = 0; i < applicationFiles.length; i++) {
      applicationForm.append('files', applicationFiles[i]);
    }
    applicationForm.append('documentType', 'Application');
    applicationForm.append('guid', this.data.submissionGuid);

    const lossRunForm = new FormData();
    const lossRunFiles = this.form.get('lossRuns')?.value;
    for (let i = 0; i < lossRunFiles.length; i++) {
      lossRunForm.append('files', lossRunFiles[i]);
    }
    lossRunForm.append('documentType', 'LossRun');
    lossRunForm.append('guid', this.data.submissionGuid);

    const uploadPromises = [];

    if (lossRunFiles.length > 0) {
      uploadPromises.push(this.viewSubmissionStore.uploadSubmissionDocument(lossRunForm, false));
    }
  
    if (applicationFiles.length > 0) {
      uploadPromises.push(this.viewSubmissionStore.uploadSubmissionDocument(applicationForm, false));
    }
    Promise.all(uploadPromises)
    const uploadAction = uploadPromises.length
    ? Promise.all(uploadPromises)
    : Promise.resolve();

    uploadAction
    .then(() => {
      //#region Bind Request
      var proposedEffectiveDate = this.form.get('proposedEffectiveDate')?.value;
      if (this.data.policyEffectiveDate == toDateOnly(proposedEffectiveDate)) {
        proposedEffectiveDate = null;
      }
      const bindRequestForm: any = {
        submissionGuid: this.data.submissionGuid,
        proposedEffectiveDate: proposedEffectiveDate,
        submissionStatus: SubmissionStatusEnum.BindRequested,
      };

      // Add agency/producer info if RetailAgentId is not set
      if (!this.data.retailAgentId) {
        bindRequestForm.agencyName = this.form.get('agencyName')?.value;
        bindRequestForm.producerName = this.form.get('producerName')?.value;
        bindRequestForm.agencyAddress1 = this.form.get('agencyAddress1')?.value;
        bindRequestForm.agencyAddress2 = this.form.get('agencyAddress2')?.value;
        bindRequestForm.agencyCity = this.form.get('agencyCity')?.value;
        bindRequestForm.agencyStateCode = this.form.get('agencyStateCode')?.value;
        bindRequestForm.agencyZipCode = this.form.get('agencyZipCode')?.value;
      }

      this.viewSubmissionStore.submitBindRequest(bindRequestForm)
      .then(() => {
        this.snackbar.openFromComponent(SnackbarComponent, {
          duration: 5000,
          data: { toastType: ToastType.Success, message: 'Your Bind Request has been sent.' },
        });
        this.dialogRef.close(true);
      })
      .catch((error) => {
        console.error('Error processing bind request:', error);
        this.snackbar.openFromComponent(SnackbarComponent, {
          duration: 5000,
          data: {
            toastType: ToastType.Error,
            message: 'An error occurred.',
          },
        });
      })
      //#endregion
    })
    .catch((error) => {
      console.error('Error during file uploads:', error);
      this.snackbar.openFromComponent(SnackbarComponent, {
        duration: 5000,
        data: {
          toastType: ToastType.Error,
          message: 'An error occurred during file uploads.',
        },
      });
    });
  }
}
